// frontend/src/pages/QuickRegPage.jsx — QR Self-Service Athlete Registration
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

// ── Division filter ─────────────────────────────────────────────────────────
// Eine Regel für alle Masken: lib/divisionsFilter.js (passendeDivisionen).
// Hier stand bis 27.09.2026 eine eigene Abschrift mit anderen Regeln (Kampf:
// bis 8 Jahre über der Klasse, Alter zum heutigen Tag). Seit der Server
// streng prüft, hätte sie Kategorien angeboten, die er ablehnt.
import { passendeDivisionen } from '../lib/divisionsFilter';

const filterForAthlete = (allDiv, profil, stichtag) => passendeDivisionen(allDiv, profil, stichtag);

// Group divisions by type for display
const DIV_TYPE_LABELS = {
  'Formen': 'Formen',
  'Selbstverteidigung': 'Selbstverteidigung',
  'Kumite': 'Kumite',
  'Continous Fighting': 'Continous Fighting',
  'Pointfighting': 'Pointfighting',
  'K1': 'K1',
  'Fullcontact': 'Fullcontact',
  'Grappling': 'Grappling',
  'Bruchtest': 'Bruchtest',
  'Rumble': 'Rumble',
};

// ── Inline styles ─────────────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0d0d1a',
    color: '#e8e0d0',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    padding: '0 0 60px',
  },
  header: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #0d0d1a 100%)',
    borderBottom: '1px solid #c8a84b33',
    padding: '24px 20px 20px',
    textAlign: 'center',
  },
  logo: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#c8a84b',
    letterSpacing: '2px',
    margin: 0,
  },
  subTitle: {
    fontSize: '13px',
    color: '#8a8070',
    marginTop: '4px',
    letterSpacing: '1px',
  },
  tournamentBadge: {
    display: 'inline-block',
    marginTop: '12px',
    background: '#1e1e3a',
    border: '1px solid #c8a84b55',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    color: '#c8a84b',
    fontWeight: '600',
  },
  container: {
    maxWidth: '520px',
    margin: '0 auto',
    padding: '24px 16px',
  },
  card: {
    background: '#13132b',
    border: '1px solid #2a2a4a',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#c8a84b',
    letterSpacing: '0.5px',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '1px solid #2a2a4a',
  },
  fieldGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    color: '#8a8070',
    marginBottom: '6px',
    fontWeight: '500',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  required: {
    color: '#c8a84b',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    background: '#0d0d1a',
    border: '1px solid #2a2a4a',
    borderRadius: '8px',
    padding: '12px 14px',
    fontSize: '15px',
    color: '#e8e0d0',
    outline: 'none',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
    transition: 'border-color 0.2s',
  },
  radioGroup: {
    display: 'flex',
    gap: '12px',
  },
  radioBtn: (active) => ({
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    border: active ? '2px solid #c8a84b' : '1px solid #2a2a4a',
    background: active ? '#1e1e3a' : '#0d0d1a',
    color: active ? '#c8a84b' : '#8a8070',
    cursor: 'pointer',
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: active ? '700' : '400',
    transition: 'all 0.15s',
  }),
  divisionList: {
    maxHeight: '320px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  divTypeHeader: {
    fontSize: '11px',
    color: '#c8a84b',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    padding: '8px 0 4px',
    fontWeight: '600',
  },
  divItem: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '8px',
    marginBottom: '4px',
    border: selected ? '1px solid #c8a84b55' : '1px solid #1e1e3a',
    background: selected ? '#1a1a2e' : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s',
  }),
  checkbox: (selected) => ({
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    border: selected ? '2px solid #c8a84b' : '2px solid #3a3a5a',
    background: selected ? '#c8a84b' : 'transparent',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  divName: {
    fontSize: '13px',
    color: '#e8e0d0',
    lineHeight: '1.3',
  },
  divCode: {
    fontSize: '11px',
    color: '#8a8070',
    marginTop: '2px',
  },
  btnPrimary: {
    width: '100%',
    background: 'linear-gradient(135deg, #c8a84b 0%, #b8943b 100%)',
    color: '#0d0d1a',
    border: 'none',
    borderRadius: '10px',
    padding: '16px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '0.5px',
    marginTop: '8px',
    transition: 'opacity 0.2s',
  },
  btnDisabled: {
    width: '100%',
    background: '#2a2a4a',
    color: '#6a6070',
    border: 'none',
    borderRadius: '10px',
    padding: '16px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'not-allowed',
    letterSpacing: '0.5px',
    marginTop: '8px',
  },
  successCard: {
    background: '#0d1a0d',
    border: '1px solid #2a5a2a',
    borderRadius: '12px',
    padding: '40px 24px',
    textAlign: 'center',
    marginTop: '24px',
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  successTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#4a9a4a',
    marginBottom: '12px',
  },
  successText: {
    fontSize: '15px',
    color: '#8a9a8a',
    lineHeight: '1.6',
  },
  errorMsg: {
    background: '#1a0d0d',
    border: '1px solid #5a2a2a',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#c05050',
    fontSize: '14px',
    marginBottom: '16px',
  },
  emptyDiv: {
    color: '#6a6070',
    fontSize: '13px',
    textAlign: 'center',
    padding: '24px',
  },
  filterNote: {
    fontSize: '12px',
    color: '#6a6070',
    marginBottom: '12px',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function QuickRegPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const vereinParam = searchParams.get('verein') || '';

  // Tournament info
  const [turnier, setTurnier] = useState(null);
  const [loadingTurnier, setLoadingTurnier] = useState(true);
  const [tournierError, setTournierError] = useState(null);

  // All divisions
  const [allDivisionen, setAllDivisionen] = useState([]);
  const [altersStichtag, setAltersStichtag] = useState(null);
  const [vereine, setVereine] = useState([]); // Aufgabe 127: Vorschläge aus allen Vereinen des Verbands

  // Form state — pre-fill verein_name from URL param if provided
  const [form, setForm] = useState({
    vorname: '',
    nachname: '',
    geburtsdatum: '',
    geschlecht: '',
    gewicht: '',
    verein_name: vereinParam,
  });
  const [selectedCodes, setSelectedCodes] = useState([]);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Load tournament info
  useEffect(() => {
    if (!token) {
      setTournierError('Kein Token angegeben.');
      setLoadingTurnier(false);
      return;
    }
    fetch(`/api/quickreg/form/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setTurnier(data.turnier);
        } else {
          setTournierError(data.error || 'Turnier nicht gefunden.');
        }
      })
      .catch(() => setTournierError('Verbindungsfehler — bitte Seite neu laden.'))
      .finally(() => setLoadingTurnier(false));
  }, [token]);

  // Divisionen über den Einladungs-Token laden — /api/divisionen selbst
  // verlangt inzwischen einen Login.
  useEffect(() => {
    if (!token) return;
    fetch(`/api/quickreg/divisionen/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data) { setAllDivisionen(data.data); setAltersStichtag(data.altersStichtag || null); }
      })
      .catch(() => {});
  }, [token]);

  // Vereine des Verbands als Vorschläge (Aufgabe 127) — fehlt die Liste,
  // bleibt das Feld ein normales Textfeld.
  useEffect(() => {
    if (!token) return;
    fetch(`/api/quickreg/vereine/${token}`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data?.success) setVereine(data.vereine || []); })
      .catch(() => {});
  }, [token]);

  // Filtered divisions based on current profile inputs
  const filteredDivisionen = useCallback(() => {
    if (allDivisionen.length === 0) return [];
    const profil = {
      geburtsdatum: form.geburtsdatum || null,
      geschlecht: form.geschlecht || null,
      gewicht: form.gewicht || null,
    };
    return filterForAthlete(allDivisionen, profil, altersStichtag);
  }, [allDivisionen, altersStichtag, form.geburtsdatum, form.geschlecht, form.gewicht]);

  // Group divisions by type
  const groupedDivisionen = useCallback(() => {
    const divs = filteredDivisionen();
    const groups = {};
    for (const div of divs) {
      const t = div.Division_Type || 'Sonstige';
      if (!groups[t]) groups[t] = [];
      groups[t].push(div);
    }
    return groups;
  }, [filteredDivisionen]);

  const handleFieldChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    // Deselect divisions that no longer match after profile change
    if (['geburtsdatum', 'geschlecht', 'gewicht'].includes(field)) {
      setSelectedCodes(prev => {
        const newForm = { ...form, [field]: value };
        const profil = {
          geburtsdatum: newForm.geburtsdatum || null,
          geschlecht: newForm.geschlecht || null,
          gewicht: newForm.gewicht || null,
        };
        const stillValid = new Set(filterForAthlete(allDivisionen, profil, altersStichtag).map(d => d.Division_Code));
        return prev.filter(c => stillValid.has(c));
      });
    }
  };

  const toggleDivision = (code) => {
    setSelectedCodes(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const canSubmit =
    form.vorname.trim() &&
    form.nachname.trim() &&
    form.geburtsdatum &&
    form.geschlecht &&
    parseFloat(form.gewicht) > 0; // Pflicht seit 16.09.2026 (routes/quickreg.js)

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch('/api/quickreg/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          vorname: form.vorname.trim(),
          nachname: form.nachname.trim(),
          geburtsdatum: form.geburtsdatum,
          geschlecht: form.geschlecht,
          gewicht: form.gewicht ? parseFloat(form.gewicht) : null,
          verein_name: form.verein_name.trim() || null,
          division_codes: selectedCodes,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setSubmitError(data.error || 'Unbekannter Fehler');
      }
    } catch {
      setSubmitError('Verbindungsfehler — bitte erneut versuchen.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loadingTurnier) {
    return (
      <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#c8a84b', fontSize: '16px' }}>Lade Turnierinformationen...</div>
      </div>
    );
  }

  if (tournierError) {
    return (
      <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={S.errorMsg}>{tournierError}</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <p style={S.logo}>TDA</p>
          <p style={S.subTitle}>TURNIERVERWALTUNG</p>
          {turnier && <span style={S.tournamentBadge}>{turnier.name}</span>}
        </div>
        <div style={S.container}>
          <div style={S.successCard}>
            <div style={S.successIcon}>✓</div>
            <div style={S.successTitle}>Anmeldung eingereicht!</div>
            <p style={S.successText}>
              Der Organisator prüft deine Daten und bestätigt dich gleich.
              Du kannst dieses Fenster schließen.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const groups = groupedDivisionen();
  const totalFiltered = filteredDivisionen().length;

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <p style={S.logo}>TDA</p>
        <p style={S.subTitle}>TURNIERVERWALTUNG</p>
        {turnier && (
          <span style={S.tournamentBadge}>
            {turnier.name}
            {turnier.datum && ` · ${new Date(turnier.datum).toLocaleDateString('de-DE')}`}
          </span>
        )}
      </div>

      <div style={S.container}>

        {/* Personal data */}
        <div style={S.card}>
          <div style={S.sectionTitle}>Deine Daten</div>

          <div style={S.fieldGroup}>
            <label style={S.label}>Vorname<span style={S.required}> *</span></label>
            <input
              style={S.input}
              type="text"
              autoComplete="given-name"
              value={form.vorname}
              onChange={e => handleFieldChange('vorname', e.target.value)}
              placeholder="z.B. Max"
            />
          </div>

          <div style={S.fieldGroup}>
            <label style={S.label}>Nachname<span style={S.required}> *</span></label>
            <input
              style={S.input}
              type="text"
              autoComplete="family-name"
              value={form.nachname}
              onChange={e => handleFieldChange('nachname', e.target.value)}
              placeholder="z.B. Mustermann"
            />
          </div>

          <div style={S.fieldGroup}>
            <label style={S.label}>Geburtsdatum<span style={S.required}> *</span></label>
            <input
              style={S.input}
              type="date"
              value={form.geburtsdatum}
              onChange={e => handleFieldChange('geburtsdatum', e.target.value)}
            />
          </div>

          <div style={S.fieldGroup}>
            <label style={S.label}>Geschlecht<span style={S.required}> *</span></label>
            <div style={S.radioGroup}>
              {['M', 'W'].map(g => (
                <button
                  key={g}
                  type="button"
                  style={S.radioBtn(form.geschlecht === g)}
                  onClick={() => handleFieldChange('geschlecht', g)}
                >
                  {g === 'M' ? 'Männlich' : 'Weiblich'}
                </button>
              ))}
            </div>
          </div>

          <div style={S.fieldGroup}>
            <label style={S.label}>Gewicht (kg) *</label>
            <input
              style={S.input}
              type="number"
              inputMode="decimal"
              min="20"
              max="200"
              step="0.1"
              value={form.gewicht}
              onChange={e => handleFieldChange('gewicht', e.target.value)}
              placeholder="z.B. 65.5"
            />
          </div>

          <div style={S.fieldGroup}>
            <label style={S.label}>Verein / Schule <span style={{ color: '#6a6070' }}>optional</span></label>
            <input
              style={S.input}
              type="text"
              value={form.verein_name}
              onChange={e => handleFieldChange('verein_name', e.target.value)}
              placeholder={vereine.length ? 'Verein suchen oder eintippen' : 'z.B. TDA München'}
              list={vereine.length ? 'quickreg-vereine' : undefined}
              autoComplete="off"
            />
            {vereine.length > 0 && (
              <datalist id="quickreg-vereine">
                {vereine.map(v => <option key={v.vereins_id} value={v.name}>{v.ort || ''}</option>)}
              </datalist>
            )}
          </div>
        </div>

        {/* Division selection */}
        <div style={S.card}>
          <div style={S.sectionTitle}>Kategorien auswählen</div>

          {!form.geschlecht && !form.geburtsdatum ? (
            <p style={S.filterNote}>
              Füll zunächst Geschlecht und Geburtsdatum aus — dann werden passende Kategorien angezeigt.
            </p>
          ) : allDivisionen.length === 0 ? (
            <p style={S.emptyDiv}>Kategorien werden geladen...</p>
          ) : totalFiltered === 0 ? (
            <p style={S.emptyDiv}>Keine passenden Kategorien gefunden.</p>
          ) : (
            <>
              <p style={S.filterNote}>
                {totalFiltered} passende Kategorie{totalFiltered !== 1 ? 'n' : ''} gefunden
                {selectedCodes.length > 0 && ` · ${selectedCodes.length} ausgewählt`}
              </p>
              <div style={S.divisionList}>
                {Object.entries(groups).map(([type, divs]) => (
                  <div key={type}>
                    <div style={S.divTypeHeader}>
                      {DIV_TYPE_LABELS[type] || type}
                    </div>
                    {divs.map(div => {
                      const code = div.Division_Code;
                      const selected = selectedCodes.includes(code);
                      return (
                        <div
                          key={code}
                          style={S.divItem(selected)}
                          onClick={() => toggleDivision(code)}
                          role="checkbox"
                          aria-checked={selected}
                          tabIndex={0}
                          onKeyDown={e => e.key === ' ' && toggleDivision(code)}
                        >
                          <div style={S.checkbox(selected)}>
                            {selected && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                <path d="M1 4L3.5 6.5L9 1" stroke="#0d0d1a" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            )}
                          </div>
                          <div>
                            <div style={S.divName}>{div.Division_Name || code}</div>
                            <div style={S.divCode}>{code}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Submit */}
        {submitError && <div style={S.errorMsg}>{submitError}</div>}

        <button
          type="button"
          style={canSubmit && !submitting ? S.btnPrimary : S.btnDisabled}
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Wird gesendet...' : 'Anmeldung absenden'}
        </button>

        {!canSubmit && (
          <p style={{ color: '#6a6070', fontSize: '12px', textAlign: 'center', marginTop: '8px' }}>
            Pflichtfelder: Vorname, Nachname, Geburtsdatum, Geschlecht, Gewicht
          </p>
        )}
      </div>
    </div>
  );
}
